import { BlockOutlined, Chat, ConfirmationNumberOutlined, EditOutlined } from '@mui/icons-material';
import {
    Chip,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';

import TicketStatusChip from './ticketStatusChip';
import Result from 'components/v2/common/Result';
import TableSkeleton from 'components/v2/common/tableSkeleton';

import { ACTIONS } from 'constant';
import { TICKET_URGENCY_CONFIG, TicketStatus } from 'data/constants/constants';
import { useAgents } from 'hooks/useAgents';
import useTranslate from 'hooks/useTranslate';
import { TicketInterface } from 'types';
import { formatDateString } from 'utils/formatDate';
import { getTicketUrgencyLabelKey } from 'utils/ticket';

interface TicketTableProps {
    tickets: TicketInterface[];
    loading?: boolean;
    handleAction: (action: ACTIONS, data?: TicketInterface) => void;
    onChangeStatus: (ticket: TicketInterface, status: TicketStatus) => void;
}

const headCellSx = {
    py: 1.25,
    px: 1.5,
    fontWeight: 700,
    bgcolor: 'grey.50',
    whiteSpace: 'nowrap' as const,
};

const bodyCellSx = {
    py: 1.5,
    px: 1.5,
    verticalAlign: 'middle' as const,
};

const TicketTable = ({ tickets, loading, handleAction, onChangeStatus }: TicketTableProps) => {
    const { translate } = useTranslate();
    const agentOptions = useAgents();
    const getAgentName = (id?: string | number): string =>
        agentOptions.find((agent) => String(agent.value) === String(id))?.label || '—';

    if (loading) return <TableSkeleton tableColumns={8} tableRowCount={6} />;

    if (!tickets.length) {
        return (
            <Result
                icon={<ConfirmationNumberOutlined sx={{ fontSize: 32 }} />}
                title={translate('noTicketsYet')}
                description={translate('noTicketsYetDescription')}
            />
        );
    }

    return (
        <Paper
            variant="outlined"
            sx={{
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                height: 'clamp(320px, calc(100vh - 340px), 720px)',
                borderRadius: 1.5,
            }}
        >
            <TableContainer sx={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
                <Table stickyHeader size="small" sx={{ minWidth: 720 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...headCellSx, width: 72 }}>ID</TableCell>
                            <TableCell sx={headCellSx}>{translate('title')}</TableCell>
                            <TableCell sx={headCellSx}>{translate('document')}</TableCell>
                            <TableCell sx={headCellSx}>{translate('description')}</TableCell>
                            <TableCell sx={headCellSx}>{translate('status')}</TableCell>
                            <TableCell sx={headCellSx}>{translate('urgency')}</TableCell>
                            <TableCell sx={headCellSx}>{translate('assignedTo')}</TableCell>
                            <TableCell sx={headCellSx}>{translate('created')}</TableCell>
                            <TableCell sx={headCellSx} align="right">
                                {translate('actions')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tickets.map((ticket) => {
                            const urgencyStyle = TICKET_URGENCY_CONFIG[ticket.urgency];
                            const isClosed = ticket.status === TicketStatus.CLOSED;

                            return (
                                <TableRow key={ticket.id} hover>
                                    <TableCell sx={bodyCellSx}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            #{ticket.id}
                                        </Typography>
                                    </TableCell>
                                    <TableCell
                                        sx={{ ...bodyCellSx, maxWidth: 200, cursor: 'pointer' }}
                                        onClick={() => handleAction(ACTIONS.VIEW, ticket)}
                                    >
                                        <Typography
                                            variant="body2"
                                            noWrap
                                            sx={{ fontWeight: 600, color: 'primary.main' }}
                                        >
                                            {ticket.text || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell
                                        sx={{ ...bodyCellSx, maxWidth: 200, cursor: 'pointer' }}
                                        onClick={() => handleAction(ACTIONS.PREVIEW_FILE, ticket)}
                                    >
                                        <Typography
                                            variant="body2"
                                            noWrap
                                            sx={{ fontWeight: 600, color: 'primary.main' }}
                                        >
                                            {ticket.document_name || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ ...bodyCellSx, maxWidth: 260 }}>
                                        <Typography variant="body2" color="text.secondary" noWrap>
                                            {ticket.description || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={bodyCellSx}>
                                        <TicketStatusChip
                                            status={ticket.status}
                                            onChangeStatus={(newStatus) =>
                                                onChangeStatus(ticket, newStatus)
                                            }
                                            disabled={isClosed}
                                        />
                                    </TableCell>
                                    <TableCell sx={bodyCellSx}>
                                        <Chip
                                            size="small"
                                            label={translate(
                                                getTicketUrgencyLabelKey(ticket.urgency)
                                            )}
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: 11,
                                                color: urgencyStyle?.color,
                                                backgroundColor: urgencyStyle?.bg ?? 'action.hover',
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={bodyCellSx}>
                                        <Typography variant="body2" color="text.secondary" noWrap>
                                            {getAgentName(ticket.assigned_to_id)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={bodyCellSx}>
                                        <Typography variant="body2" color="text.secondary" noWrap>
                                            {formatDateString(ticket.created)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={bodyCellSx} align="right">
                                        <Stack
                                            direction="row"
                                            spacing={0.25}
                                            sx={{ justifyContent: 'flex-end' }}
                                        >
                                            <Tooltip title={translate('ticketChat')}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        handleAction(ACTIONS.CHAT, ticket)
                                                    }
                                                >
                                                    <Chat fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={translate('editTicket')}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        disabled={isClosed}
                                                        onClick={() =>
                                                            handleAction(ACTIONS.EDIT, ticket)
                                                        }
                                                    >
                                                        <EditOutlined fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={translate('closeTicket')}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="warning"
                                                        disabled={isClosed}
                                                        onClick={() =>
                                                            handleAction(ACTIONS.CLOSE, ticket)
                                                        }
                                                    >
                                                        <BlockOutlined fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default TicketTable;
